import FeatureRequestCard from "@/src/components/feature-requests/feature-request-card";
import FeatureRequestCardSkeleton from "@/src/components/feature-requests/feature-request-card-skeleton";
import {
  FeatureRequest,
  FeatureRequestStatus,
} from "@/src/models/feature-request.model";
import {
  FeatureRequestScope,
  useFeatureRequestColumn,
} from "@/src/services/feature-request/feature-request.hooks";
import { FEATURE_REQUEST_STATUS_STYLES } from "@/src/utils/feature-request.utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, Text, View } from "react-native";

export const COLUMN_WIDTH = 320;

const INITIAL_SKELETON_COUNT = 3;

type FeatureRequestColumnProps = Readonly<{
  status: FeatureRequestStatus;
  scope: FeatureRequestScope;
  enabled: boolean;
  /** Board height available to the column; its card list scrolls within it. */
  maxHeight?: number;
  onCardPress: (request: FeatureRequest) => void;
}>;

export default function FeatureRequestColumn({
  status,
  scope,
  enabled,
  maxHeight,
  onCardPress,
}: FeatureRequestColumnProps) {
  const { t } = useTranslation();
  const style = FEATURE_REQUEST_STATUS_STYLES[status];

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFeatureRequestColumn(status, scope, enabled);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  const renderFooter = () => {
    if (isLoading) {
      return (
        <View className="gap-2">
          {Array.from({ length: INITIAL_SKELETON_COUNT }, (_, i) => (
            <FeatureRequestCardSkeleton key={i} />
          ))}
        </View>
      );
    }
    if (isFetchingNextPage) {
      return (
        <View className="pt-2">
          <FeatureRequestCardSkeleton />
        </View>
      );
    }
    if (isError) {
      return (
        <Text className="py-3 text-sm text-center text-crimson">
          {t("ERROR_FEATURE_REQUESTS_LOAD_FAILED")}
        </Text>
      );
    }
    if (items.length === 0) {
      return (
        <Text className="py-3 text-sm text-center text-ash-1">
          {t("LABEL_FEATURE_REQUESTS_EMPTY_COLUMN")}
        </Text>
      );
    }
    return null;
  };

  return (
    // Shrinks to its cards (a short column stays short, as in the design) and
    // caps at the board height, where the card list takes over scrolling.
    <View
      style={{ width: COLUMN_WIDTH, maxHeight }}
      className="self-start gap-2 p-2 rounded-3xl bg-obsidian"
    >
      <View
        className={`flex-row items-center gap-2 px-2 py-2 rounded-full ${style.pillClassName}`}
      >
        <View className="items-center justify-center h-7 px-2 rounded-full min-w-7 bg-chalk">
          {/* Blank while loading — a "0" would read as an empty column. */}
          <Text className={`text-xs font-semibold ${style.countClassName}`}>
            {isLoading ? "" : totalCount}
          </Text>
        </View>
        <Text className="text-base font-semibold text-white">
          {t(style.labelKey)}
        </Text>
      </View>

      <FlatList
        data={items}
        keyExtractor={(item) => item.ItemId}
        renderItem={({ item }) => (
          <FeatureRequestCard request={item} onPress={onCardPress} />
        )}
        contentContainerClassName="gap-2"
        showsVerticalScrollIndicator={false}
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage) fetchNextPage();
        }}
        onEndReachedThreshold={0.5}
        ListFooterComponent={renderFooter}
        style={{ flexShrink: 1, minHeight: 0 }}
      />
    </View>
  );
}
